// Iter 145 — Confidence Trend analytics page (v3 teardown).

import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import TopNav from "../components/TopNav";
import ConfidenceTrendSparkline from "../components/ConfidenceTrendSparkline";
import { API } from "../App";
import { Loader2, TrendingDown, TrendingUp, Activity } from "lucide-react";
import { countryToFlag } from "../lib/countryFlag";
import {
  V3Page, V3Container, V3Header, V3Eyebrow, V3Segment, V3Num, V3Tag,
} from "../components/v3";

const WINDOWS = [14, 30, 60, 90];

const AnalyticsConfidenceTrendPage = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    axios
      .get(`${API}/analytics/confidence-trend?days=${days}&limit=20`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) toast.error("Couldn't load analytics"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const leagues = data?.leagues || [];
  const sorted = [...leagues].sort(
    (a, b) => Math.abs(b.drift_pp) - Math.abs(a.drift_pp)
  );

  return (
    <V3Page>
      <TopNav />
      <V3Container>
        <V3Header
          testid="analytics-trend-title"
          eyebrow={<V3Eyebrow icon={Activity}>Model drift monitor</V3Eyebrow>}
          title="Confidence trend."
          titleAccent="Per-league hit-rate vs baseline."
          subtitle="Leagues whose last-7-day hit-rate has drifted away from the window baseline are surfaced first so regressions get spotted early."
          right={
            <V3Segment
              value={days}
              onChange={setDays}
              testidPrefix="analytics-trend-window"
              options={WINDOWS.map((w) => ({ value: w, label: `${w}d`, testid: `analytics-trend-window-${w}` }))}
            />
          }
        />

        {loading && (
          <div
            className="flex items-center gap-2 py-10"
            style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
            data-testid="analytics-trend-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading trend analytics…
          </div>
        )}

        {!loading && !sorted.length && (
          <div
            className="p-10 text-center"
            style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}
            data-testid="analytics-trend-empty"
          >
            <p className="text-base" style={{ fontWeight: 500 }}>No settled samples in this window</p>
            <p className="mt-2 text-[13px]" style={{ color: "var(--v3-text-3)" }}>
              Try a longer window or wait for the next settlement run.
            </p>
          </div>
        )}

        {sorted.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ borderTop: "1px solid var(--v3-line)" }}>
            {sorted.map((lg, i) => {
              const flag = countryToFlag(lg.country) || "🌍";
              const Icon = lg.drift_pp > 0 ? TrendingUp : TrendingDown;
              const driftTone =
                lg.drift_pp >= 3 ? "emerald"
                : lg.drift_pp <= -3 ? "rose"
                : "mute";
              return (
                <div
                  key={lg.league_id}
                  className="p-5"
                  style={{
                    borderBottom: "1px solid var(--v3-line)",
                    borderRight: i % 2 === 0 ? "1px solid var(--v3-line)" : "none",
                  }}
                  data-testid={`analytics-trend-league-${lg.league_id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-base leading-none">{flag}</span>
                        <span className="truncate text-[14px]" style={{ fontWeight: 500 }}>{lg.league_name}</span>
                      </div>
                      <div
                        className="text-[11px] mt-1"
                        style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
                      >
                        {lg.country || "—"} · {lg.total_settled} settled · avg conf {lg.avg_confidence}%
                      </div>
                    </div>
                    <V3Tag tone={driftTone} testid={`analytics-trend-drift-${lg.league_id}`}>
                      <Icon className="w-3 h-3" />
                      {lg.drift_pp > 0 ? "+" : ""}{lg.drift_pp}pp
                    </V3Tag>
                  </div>
                  <ConfidenceTrendSparkline leagueId={lg.league_id} days={days} />
                  <div
                    className="flex items-center gap-3 mt-3 text-[11px]"
                    style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
                  >
                    <span>
                      Overall <V3Num tone="var(--v3-indigo-fg)">{lg.overall_hit_rate}%</V3Num>
                    </span>
                    <span style={{ color: "var(--v3-text-4)" }}>·</span>
                    <span>
                      Recent 7d <V3Num>{lg.recent_hit_rate}%</V3Num>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </V3Container>
    </V3Page>
  );
};

export default AnalyticsConfidenceTrendPage;
